import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { complianceCheck, pcpReviewStatus } from "../lib/roles";
import {
  LayoutDashboard, BadgeCheck, Clock, AlertTriangle, CalendarClock, FileText,
} from "lucide-react";

export default function QpSummary({ onGoTeam, onGoRenewals }) {
  const [notes, setNotes] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([api.get("/notes/all"), api.get("/consumers")])
      .then(([a, b]) => { setNotes(a.data); setConsumers(b.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // keep counts live during the day
    const onFocus = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", load);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", load);
    };
  }, []);

  const s = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86400000;
    const thisWeek = notes.filter((n) => new Date(n.created_at || 0).getTime() >= weekAgo);
    const signed = notes.filter((n) => n.status === "reviewed");
    const awaiting = notes.filter((n) => n.status !== "reviewed");
    const unbillable = notes.filter((n) => !complianceCheck(n.structured).complete);
    const dueList = consumers
      .map((c) => ({ c, r: pcpReviewStatus(c) }))
      .filter((x) => x.r && x.r.level !== "ok")
      .sort((a, b) => a.r.days - b.r.days);
    return { thisWeek: thisWeek.length, signed: signed.length, awaiting, unbillable: unbillable.length, dueList };
  }, [notes, consumers]);

  const Card = ({ icon: Icon, label, value, color, testid, onClick }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className="text-left bg-white border border-[#E2DFD8] rounded-2xl p-5 shadow-sm hover:border-[#4A6B53] transition-colors"
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color }} />
        <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-display text-4xl font-extrabold" style={{ color }}>{value}</p>
    </button>
  );

  return (
    <div data-testid="qp-summary">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
          <LayoutDashboard className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">
            This Week at a Glance
          </h1>
          <p className="text-sm text-[#7A8C82]">Your caseload's notes and PCP renewals in one place.</p>
        </div>
      </div>

      {loading && <p className="text-sm text-[#7A8C82]">Loading…</p>}

      {!loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Card testid="summary-week" icon={FileText} label="Notes this week" value={s.thisWeek} color="#4A6B53" />
            <Card testid="summary-signed" icon={BadgeCheck} label="Signed off" value={s.signed} color="#4A6B53" />
            <Card testid="summary-awaiting" icon={Clock} label="Awaiting sign-off" value={s.awaiting.length} color="#D4A373" onClick={onGoTeam} />
            <Card testid="summary-unbillable" icon={AlertTriangle} label="Unbillable" value={s.unbillable} color="#C26D5C" onClick={onGoTeam} />
            <Card testid="summary-pcps-due" icon={CalendarClock} label="PCPs due" value={s.dueList.length} color="#C26D5C" onClick={onGoRenewals} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E2DFD8] rounded-2xl p-5 shadow-sm">
              <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-[0.2em] mb-3 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" strokeWidth={2} /> Awaiting your sign-off ({s.awaiting.length})
              </p>
              {s.awaiting.length === 0 && <p className="text-sm text-[#7A8C82]">All caught up — nothing awaiting.</p>}
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {s.awaiting.slice(0, 20).map((n) => (
                  <div key={n.id} data-testid={`summary-await-${n.id}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-[#F9F9F6] border border-[#E2DFD8]">
                    <span className="text-sm text-[#121814] truncate">{n.structured?.member_name || "Unnamed"}</span>
                    <span className="text-xs text-[#7A8C82] shrink-0">{n.staff_name} · {n.structured?.date_of_service || ""}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#E2DFD8] rounded-2xl p-5 shadow-sm">
              <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-[0.2em] mb-3 flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5" strokeWidth={2} /> PCP renewals due ({s.dueList.length})
              </p>
              {s.dueList.length === 0 && <p className="text-sm text-[#7A8C82]">No renewals due right now.</p>}
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {s.dueList.map(({ c, r }) => (
                  <div key={c.id} data-testid={`summary-due-${c.id}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-[#F9F9F6] border border-[#E2DFD8]">
                    <span className="text-sm text-[#121814] truncate">{c.name}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded shrink-0 ${r.level === "overdue" ? "bg-[#C26D5C] text-white" : "bg-[#F6E9C9] text-[#8a6d1f]"}`}>{r.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
